import type { WorkflowData } from "../types/workflow.types";

export const sanitizeWorkflowForBackend = (workflow: WorkflowData): any => ({
  ...workflow,
  nodes: workflow?.nodes?.map(sanitizeNode) ?? [],
  edges: workflow?.edges?.map(sanitizeEdge) ?? [],
});

const sanitizeNode = (node: any) => ({
  id: node.id,
  type: node.type,
  position: node.position,
  data: {
    label: node.data.label,
    type: node.data.type,
    category: node.data.category,
    nodeKey: node.data.nodeKey,
    icon: node.data.icon,
    color: node.data.color,
    config: node.data.config || {},
  },
});

const sanitizeEdge = (edge: any) => ({
  id: edge.id,
  source: edge.source,
  target: edge.target,
  sourceHandle: edge.sourceHandle ?? null,
  targetHandle: edge.targetHandle ?? null,
});

export const hydrateWorkflowFromBackend = (
  workflow: any,
  nodeConfigs: any,
  nodeDynamicDataMap: Record<string, any> = {}
): WorkflowData => {
  if (!workflow)
    return { name: "Untitled Workflow", nodes: [], edges: [], version: 1 };

  const workflowNodes = Array.isArray(workflow.nodes) ? workflow.nodes : [];
  const workflowEdges = Array.isArray(workflow.edges) ? workflow.edges : [];

  const allConfigs = [
    ...(nodeConfigs?.triggers || []),
    ...(nodeConfigs?.conditions || []),
    ...(nodeConfigs?.actions || []),
  ];

  const hydratedNodes = workflowNodes.map((node: any) => {
    if (!node?.data?.nodeKey) return node;

    const nodeConfig = allConfigs.find(
      (config) => config.key === node.data.nodeKey
    );
    if (!nodeConfig) return node;

    const dynamicData = nodeDynamicDataMap[node.data.nodeKey] || {};

    return {
      ...node,
      data: {
        ...node.data,
        fields: nodeConfig.fields || [],
        contextFields: nodeConfig.contextFields || [],
        config: node.data.config || {},
        dynamicData: dynamicData.dynamicData || {},
      },
    };
  });

  const hydratedEdges = workflowEdges.map((edge: any) =>
    edge
      ? {
          ...edge,
          type: "custom",
          animated: true,
          style: { stroke: "#404040", strokeWidth: 2 },
        }
      : edge
  );

  return {
    ...workflow,
    nodes: hydratedNodes,
    edges: hydratedEdges,
  };
};

export const canHydrateWorkflow = (
  workflow: WorkflowData,
  nodeConfigs: any
): { valid: boolean; missingNodes: string[] } => {
  if (!workflow?.nodes || !nodeConfigs)
    return { valid: false, missingNodes: [] };

  const allConfigs = [
    ...(nodeConfigs.triggers || []),
    ...(nodeConfigs.conditions || []),
    ...(nodeConfigs.actions || []),
  ];

  const missingNodes = workflow.nodes
    .filter(
      (node) =>
        node?.data?.nodeKey &&
        !allConfigs.some((config) => config.key === node.data.nodeKey)
    )
    .map((node) => node.data.nodeKey);

  return { valid: missingNodes.length === 0, missingNodes };
};
